import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  useTheme,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

// Components
import QuoteForm from "../components/Forms/QuoteForm";

const QuotePage = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [emailNotification, setEmailNotification] = useState(false);

  // Sayfa başlığını güncelle ve sayfanın en üstüne kaydır
  useEffect(() => {
    // Sayfa başlığını güncelle
    document.title =
      "SGT Global Trade | " +
      (i18n.language === "tr" ? "Teklif Al" : "Get a Quote");

    // Sayfanın en üstüne kaydır
    window.scrollTo(0, 0);
  }, [i18n.language]);

  // Handle form data and create mailto link
  const handleEmailRedirect = (values) => {
    // Create email subject
    const subject = encodeURIComponent(
      i18n.language === "tr"
        ? "SGT Global Trade - Teklif Talebi"
        : "SGT Global Trade - Quote Request"
    );

    // Create email body from form values
    let body = "";
    for (const [key, value] of Object.entries(values)) {
      body += `${key}: ${value}\n`;
    }

    const encodedBody = encodeURIComponent(body);

    // Create mailto link
    const mailtoLink = `mailto:info@sgttr.com?subject=${subject}&body=${encodedBody}`;

    // Open user's email client
    window.location.href = mailtoLink;

    // Show notification
    setEmailNotification(true);
  };

  return (
    <Box sx={{ py: { xs: 4, md: 8 }, backgroundColor: "#FCFCFC" }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    mb: 3,
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                  }}
                >
                  {i18n.language === "tr" ? "Teklif Alın" : "Get a Quote"}
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  {i18n.language === "tr"
                    ? "Ürünlerimiz hakkında detaylı bilgi ve fiyat teklifi almak için yanındaki formu doldurabilirsiniz. Uzman ekibimiz en kısa sürede sizinle iletişime geçecektir."
                    : "You can fill out the form on the right to get detailed information and price quotes about our products. Our expert team will contact you as soon as possible."}
                </Typography>

                <Box
                  component={Paper}
                  elevation={2}
                  sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 2,
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                    }}
                  >
                    {i18n.language === "tr"
                      ? "Neden Bizi Tercih Etmelisiniz?"
                      : "Why Choose Us?"}
                  </Typography>

                  <Box component="ul" sx={{ pl: 2 }}>
                    <Box component="li" sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        {i18n.language === "tr"
                          ? "Premium kalitede tarım ürünleri ve bitkisel yağlar"
                          : "Premium quality agricultural products and vegetable oils"}
                      </Typography>
                    </Box>
                    <Box component="li" sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        {i18n.language === "tr"
                          ? "Rekabetçi fiyatlar ve esneklik"
                          : "Competitive prices and flexibility"}
                      </Typography>
                    </Box>
                    <Box component="li" sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        {i18n.language === "tr"
                          ? "Güvenilir ve zamanında teslimat"
                          : "Reliable and timely delivery"}
                      </Typography>
                    </Box>
                    <Box component="li" sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        {i18n.language === "tr"
                          ? "Uluslararası ticaret tecrübesi"
                          : "International trade experience"}
                      </Typography>
                    </Box>
                    <Box component="li">
                      <Typography variant="body2">
                        {i18n.language === "tr"
                          ? "Kişiselleştirilmiş müşteri hizmeti"
                          : "Personalized customer service"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  component={Paper}
                  elevation={2}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {i18n.language === "tr"
                      ? "İletişim Bilgilerimiz"
                      : "Our Contact Information"}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>
                      {i18n.language === "tr" ? "Adres:" : "Address:"}
                    </strong>{" "}
                    {i18n.language === "tr"
                      ? "İnkılap Mh. Seyit Onbaşı Cd. No: 7/A Ümraniye / İstanbul"
                      : "Inkilap District, Seyit Onbasi Street No: 7/A Umraniye / Istanbul, Turkey"}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>
                      {i18n.language === "tr" ? "Telefon:" : "Phone:"}
                    </strong>{" "}
                    +90 533 399 2188
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>
                      {i18n.language === "tr" ? "E-posta:" : "Email:"}
                    </strong>{" "}
                    info@sgttr.com
                  </Typography>

                  <Typography variant="body2">
                    <strong>
                      {i18n.language === "tr"
                        ? "Çalışma Saatleri:"
                        : "Working Hours:"}
                    </strong>{" "}
                    {i18n.language === "tr"
                      ? "09:00 - 18:00 (Haftanın 7 günü)"
                      : "09:00 - 18:00 (7 days a week)"}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={7}>
            <QuoteForm onEmailRedirect={handleEmailRedirect} />
          </Grid>
        </Grid>
      </Container>

      {/* Email notification */}
      <Snackbar
        open={emailNotification}
        autoHideDuration={6000}
        onClose={() => setEmailNotification(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setEmailNotification(false)}
          severity="info"
          sx={{ width: "100%" }}
        >
          {i18n.language === "tr"
            ? "E-posta istemcinize yönlendiriliyorsunuz. Lütfen e-postayı göndermeyi tamamlayın."
            : "You are being redirected to your email client. Please complete sending the email."}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuotePage;
